import { ReactElement } from "react";
import Head from "next/head";
import Image from "next/image";

import Hero from "@/components/intermediaries/Hero";
import HowItWorksCarousel from "@/components/landing-old/HowItWorksCarousel";
import { MainLayout } from "@/containers/layout/LandingLayout";
import CompanyFeatures from "@/components/intermediaries/CompanyFeatures";
import PartnerWithKeyzy from "@/components/intermediaries/PartnerWithKeyzy";
import Faq from "@/components/intermediaries/Faq";
import { useWindowSize } from "@/shared/hooks/useWindowSize";
import Screen from "@/constants/screens";
import ContactBanner from "@/components/intermediaries/ContactBanner";
import Testimonials from "@/components/intermediaries/Testimonials";
import getConfig from "next/config";

const Intermediaries = () => {
  const { width } = useWindowSize();

  const { publicRuntimeConfig } = getConfig();

  return (
    <>
      <Head>
        <title>Keyzy Network for Intermediaries with Clients Who Want to Buy A Home</title>
        <link rel="canonical" href="https://www.keyzy.com/intermediaries" />
        <meta
          name="description" key="description"
          content="Our zero deposit, rent to buy solution provides an easy, flexible and fast offer that partners can rely on. Learn how we work with real estate professionals."
        />
        <meta
          name="twitter:card"
          content="Our zero deposit, rent to buy solution provides an easy, flexible and fast offer that partners can rely on. Learn how we work with real estate professionals."
          key="twcard"
        />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content="/" key="ogurl" />
        <meta property="og:image" content="/about/keyzy-meta-intermediaries.png" key="ogimage" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta
          property="og:title"
          content="Keyzy Network for Intermediaries with Clients Who Want to Buy A Home"
          key="ogtitle"
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Our zero deposit, rent to buy solution provides an easy, flexible and fast offer that partners can rely on. Learn how we work with real estate professionals."
          key="ogdesc"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://keyzy.com/" },
                { "@type": "ListItem", "position": 2, "name": "Intermediaries", "item": "https://keyzy.com/intermediaries" },
              ],
            }),
          }}
        />
      </Head>

      <div className="bg-landing-ivory">
        <Hero />
        <CompanyFeatures />
        <Testimonials />
        <HowItWorksCarousel theme="light" carouselType="intermediaries" />
        <div className="relative w-full h-6 px-5 my-8 md:inline-block xl:hidden md:px-6 lg:px-11 xl:px-20 bg-landing-ivory">
          <Image
            src={`/landing/${
              width < Screen.Tablet_768 ? "mobile-doodle.svg" : "tablet-doodle.svg"
            }`}
            alt="doodle"
            layout="fill"
            objectFit="contain"
          />
        </div>
        <PartnerWithKeyzy />
        <Faq />
        <ContactBanner />
      </div>
    </>
  );
};

Intermediaries.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout theme="ivory">{page}</MainLayout>;
};

export default Intermediaries;
